import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import assetService from '@/services/asset.service';
import assetInsuranceService from '@/services/asset-insurance.service';
import { formatAssetOption } from '@/utils/assetDisplay';

const { Title } = Typography;
const { TextArea } = Input;

const CLAIM_STATUSES = ['open', 'submitted', 'in_review', 'paid', 'rejected', 'closed'] as const;

function useAssetOptions() {
  const q = useQuery({ queryKey: ['assets', 'pick'], queryFn: () => assetService.list() });
  const options = useMemo(
    () => (q.data ?? []).map((a) => ({ value: a.id, label: formatAssetOption(a) })),
    [q.data]
  );
  return { options, loading: q.isLoading };
}

export const AssetInsurancePolicyFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options: assetOptions } = useAssetOptions();

  const existingQ = useQuery({
    queryKey: ['asset-insurance-policy', id],
    queryFn: () => assetInsuranceService.getPolicy(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        asset_id: sp.get('asset') || undefined,
        is_active: true,
      });
      return;
    }
    const r = existingQ.data;
    if (!r) return;
    form.setFieldsValue({
      policy_number: r.policy_number,
      insurer_name: r.insurer_name,
      coverage_summary: r.coverage_summary,
      premium_amount: r.premium_amount,
      start_date: r.start_date ? dayjs(r.start_date) : undefined,
      end_date: r.end_date ? dayjs(r.end_date) : undefined,
      is_active: r.is_active,
      notes: r.notes,
    });
  }, [isNew, existingQ.data, form, sp]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (isNew) return assetInsuranceService.createPolicy(body);
      return assetInsuranceService.updatePolicy(id!, body);
    },
    onSuccess: () => {
      message.success(isNew ? 'Policy saved' : 'Policy updated');
      navigate('/asset/insurance');
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  if (!isNew && existingQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New policy' : 'Edit policy'}
          documentNumber={existingQ.data?.policy_number ?? ''}
        />
      }
    >
      <Title level={5}>Insurance policy</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          const body: Record<string, unknown> = {
            policy_number: v.policy_number,
            insurer_name: v.insurer_name ?? null,
            coverage_summary: v.coverage_summary ?? null,
            premium_amount: v.premium_amount != null ? Number(v.premium_amount) : null,
            start_date: v.start_date ? (v.start_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            end_date: v.end_date ? (v.end_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            is_active: v.is_active,
            notes: v.notes || null,
          };
          if (isNew) body.asset_id = v.asset_id;
          mut.mutate(body);
        }}
      >
        {isNew && (
          <Form.Item name="asset_id" label="Asset" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={assetOptions} />
          </Form.Item>
        )}
        <Form.Item name="policy_number" label="Policy number" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="insurer_name" label="Insurer">
          <Input />
        </Form.Item>
        <Form.Item name="coverage_summary" label="Coverage / sum insured (text)">
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="premium_amount" label="Premium">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="start_date" label="Start">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="end_date" label="End / expiry">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
          <Switch />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isNew ? 'Create' : 'Save'}
          </Button>
          <Button onClick={() => navigate('/asset/insurance')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};

export const AssetInsuranceClaimFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [sp] = useSearchParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { options: assetOptions } = useAssetOptions();
  const claimAssetId = Form.useWatch('asset_id', form);

  const existingQ = useQuery({
    queryKey: ['asset-insurance-claim', id],
    queryFn: () => assetInsuranceService.getClaim(id!),
    enabled: !isNew,
  });

  const assetIdForPolicies = isNew ? claimAssetId : existingQ.data?.asset_id;
  const { data: policiesForAsset = [] } = useQuery({
    queryKey: ['asset-insurance', 'policies', assetIdForPolicies],
    queryFn: () => assetInsuranceService.listPolicies({ asset_id: assetIdForPolicies }),
    enabled: Boolean(assetIdForPolicies),
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({
        asset_id: sp.get('asset') || undefined,
        status: 'open',
      });
      return;
    }
    const r = existingQ.data;
    if (!r) return;
    form.setFieldsValue({
      asset_id: r.asset_id,
      policy_id: r.policy_id,
      incident_date: r.incident_date ? dayjs(r.incident_date) : undefined,
      description: r.description,
      claim_amount: r.claim_amount,
      status: r.status,
      amount_paid: r.amount_paid,
      notes: r.notes,
    });
  }, [isNew, existingQ.data, form, sp]);

  const mut = useMutation({
    mutationFn: async (body: Record<string, unknown>) => {
      if (isNew) return assetInsuranceService.createClaim(body);
      return assetInsuranceService.updateClaim(id!, body);
    },
    onSuccess: () => {
      message.success(isNew ? 'Claim saved' : 'Claim updated');
      navigate('/asset/insurance');
    },
    onError: (e: Error) => message.error(e.message || 'Failed'),
  });

  if (!isNew && existingQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isNew ? 'New claim' : 'Edit claim'}
          documentNumber={existingQ.data?.status ? String(existingQ.data.status).replace(/_/g, ' ') : ''}
        />
      }
    >
      <Title level={5}>Insurance claim</Title>
      <Form
        form={form}
        layout="vertical"
        style={{ maxWidth: 640 }}
        onFinish={(v) => {
          const body: Record<string, unknown> = {
            policy_id: v.policy_id || null,
            incident_date: v.incident_date ? (v.incident_date as dayjs.Dayjs).format('YYYY-MM-DD') : null,
            description: v.description || null,
            claim_amount: v.claim_amount != null ? Number(v.claim_amount) : null,
            status: v.status,
            amount_paid: v.amount_paid != null ? Number(v.amount_paid) : null,
            notes: v.notes || null,
          };
          if (isNew) body.asset_id = v.asset_id;
          mut.mutate(body);
        }}
      >
        {isNew && (
          <Form.Item name="asset_id" label="Asset" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={assetOptions}
              onChange={() => form.setFieldValue('policy_id', undefined)}
            />
          </Form.Item>
        )}
        <Form.Item name="policy_id" label="Link to policy (optional)">
          <Select
            allowClear
            options={policiesForAsset.map((p) => ({
              value: p.id,
              label: `${p.policy_number} (${p.end_date || '—'})`,
            }))}
          />
        </Form.Item>
        <Form.Item name="incident_date" label="Incident date">
          <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="claim_amount" label="Claim amount">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="status" label="Status" initialValue="open">
          <Select
            options={CLAIM_STATUSES.map((s) => ({
              value: s,
              label: s.replace(/_/g, ' '),
            }))}
          />
        </Form.Item>
        <Form.Item name="amount_paid" label="Amount paid (if any)">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="notes" label="Notes">
          <TextArea rows={2} />
        </Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={mut.isPending}>
            {isNew ? 'Create' : 'Save'}
          </Button>
          <Button onClick={() => navigate('/asset/insurance')}>Cancel</Button>
        </Space>
      </Form>
    </WorkspaceLayout>
  );
};
